import React, { FormEvent, useEffect, useRef, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { isLoggedIn } from '../utils/api'
import './ApplicationPage.css'

type CustomSelectProps = {
  name: string
  placeholder: string
  options: string[]
}

const CustomSelect: React.FC<CustomSelectProps> = ({ name, placeholder, options }) => {
  const [value, setValue] = useState('')
  const [open, setOpen] = useState(false)
  const wrapRef = useRef<HTMLDivElement>(null)
  const selectRef = useRef<HTMLSelectElement>(null)

  useEffect(() => {
    const close = (event: MouseEvent) => {
      if (!wrapRef.current?.contains(event.target as Node)) setOpen(false)
    }
    document.addEventListener('mousedown', close)
    return () => document.removeEventListener('mousedown', close)
  }, [])

  return (
    <div className={`app-custom-select${open ? ' is-open' : ''}`} ref={wrapRef}>
      <select
        ref={selectRef}
        className="app-native-select"
        name={name}
        value={value}
        onChange={(event) => setValue(event.target.value)}
        required
        tabIndex={-1}
        aria-hidden="true"
      >
        <option value="">{placeholder}</option>
        {options.map((option) => <option key={option} value={option}>{option}</option>)}
      </select>
      <button
        className={`app-select-trigger${value ? ' has-value' : ''}`}
        type="button"
        onClick={() => setOpen((current) => !current)}
        aria-haspopup="listbox"
        aria-expanded={open}
      >
        <span>{value || placeholder}</span>
        <i>⌄</i>
      </button>
      {open && (
        <div className="app-select-menu" role="listbox">
          {options.map((option) => (
            <button
              className={value === option ? 'is-selected' : ''}
              type="button"
              role="option"
              aria-selected={value === option}
              key={option}
              onClick={() => {
                setValue(option)
                selectRef.current?.setCustomValidity('')
                setOpen(false)
              }}
            >
              <span>{option}</span>{value === option && <b>✓</b>}
            </button>
          ))}
        </div>
      )}
    </div>
  )
}

const ApplicationPage: React.FC = () => {
  const navigate = useNavigate()
  const [submitting, setSubmitting] = useState(false)

  useEffect(() => {
    if (!isLoggedIn()) navigate('/login', { replace: true })
  }, [navigate])

  const handleSubmit = (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault()
    if (submitting) return
    const form = new FormData(event.currentTarget)
    localStorage.setItem('loanApplication', JSON.stringify(Object.fromEntries(form.entries())))
    setSubmitting(true)
    window.setTimeout(() => navigate('/review', { replace: true }), 1500)
  }

  return (
    <div className="app-form-root">
      <header className="app-form-header">
        <button type="button" onClick={() => navigate('/')}>← Kembali</button>
        <strong>Pengajuan Pinjaman</strong>
        <span>1/2</span>
      </header>

      <main className="app-form-main">
        <div className="app-form-intro">
          <span className="app-form-step">LANGKAH 1 DARI 2</span>
          <h1>Lengkapi Data Pribadi</h1>
          <p>Pastikan data sesuai dengan KTP dan rekening bank Anda.</p>
        </div>

        <form
          className="app-form-card"
          onSubmit={handleSubmit}
          onInvalid={(event) => {
            const field = event.target as HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement
            field.setCustomValidity(
              field.validity.valueMissing
                ? 'Kolom ini wajib diisi.'
                : 'Masukkan data dengan format yang benar.',
            )
          }}
          onInput={(event) => {
            const field = event.target as HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement
            field.setCustomValidity('')
          }}
        >
          <section>
            <div className="app-section-title"><span>👤</span><div><h2>Identitas Diri</h2><p>Data pemohon sesuai dokumen resmi</p></div></div>
            <div className="app-fields app-fields-two">
              <label className="app-field app-field-full"><span>Nama lengkap sesuai KTP</span><input name="fullName" required placeholder="Masukkan nama lengkap" autoComplete="name" /></label>
              <label className="app-field"><span>NIK</span><input name="nik" required inputMode="numeric" pattern="[0-9]{16}" maxLength={16} placeholder="16 digit NIK" /></label>
              <label className="app-field"><span>Tanggal lahir</span><input name="birthDate" required type="date" /></label>
              <div className="app-field"><span>Jenis kelamin</span><CustomSelect name="gender" placeholder="Pilih jenis kelamin" options={['Laki-laki', 'Perempuan']} /></div>
              <div className="app-field"><span>Status perkawinan</span><CustomSelect name="maritalStatus" placeholder="Pilih status" options={['Belum menikah', 'Menikah', 'Cerai']} /></div>
            </div>
          </section>

          <section>
            <div className="app-section-title"><span>💼</span><div><h2>Pekerjaan dan Penghasilan</h2><p>Untuk membantu penilaian kemampuan membayar</p></div></div>
            <div className="app-fields app-fields-two">
              <div className="app-field"><span>Pekerjaan</span><CustomSelect name="occupation" placeholder="Pilih pekerjaan" options={['Karyawan swasta', 'Pegawai negeri', 'Wiraswasta', 'Profesional', 'Lainnya']} /></div>
              <div className="app-field"><span>Penghasilan per bulan</span><CustomSelect name="monthlyIncome" placeholder="Pilih penghasilan" options={['Di bawah Rp3 juta', 'Rp3–5 juta', 'Rp5–10 juta', 'Di atas Rp10 juta']} /></div>
            </div>
          </section>

          <section>
            <div className="app-section-title"><span>📍</span><div><h2>Alamat Domisili</h2><p>Alamat tempat tinggal saat ini</p></div></div>
            <div className="app-fields app-fields-two">
              <label className="app-field app-field-full"><span>Alamat lengkap</span><textarea name="address" required rows={3} placeholder="Nama jalan, nomor rumah, RT/RW, kelurahan" /></label>
              <div className="app-field"><span>Provinsi</span><CustomSelect name="province" placeholder="Pilih provinsi" options={['DKI Jakarta', 'Jawa Barat', 'Jawa Tengah', 'Jawa Timur', 'Banten', 'Sumatera Utara', 'Sumatera Selatan', 'Bali', 'Lainnya']} /></div>
              <label className="app-field"><span>Kota/Kabupaten</span><input name="city" required placeholder="Masukkan kota/kabupaten" /></label>
            </div>
          </section>

          <section>
            <div className="app-section-title"><span>🏦</span><div><h2>Rekening Pencairan</h2><p>Rekening wajib atas nama pemohon</p></div></div>
            <div className="app-fields app-fields-two">
              <div className="app-field"><span>Nama bank</span><CustomSelect name="bank" placeholder="Pilih bank" options={['BCA', 'BRI', 'BNI', 'Mandiri', 'CIMB Niaga', 'Bank lainnya']} /></div>
              <label className="app-field"><span>Nomor rekening</span><input name="bankAccount" required inputMode="numeric" pattern="[0-9]{8,20}" placeholder="Masukkan nomor rekening" /></label>
            </div>
          </section>

          <label className="app-consent"><input type="checkbox" required /><span>Saya menyatakan data yang diberikan benar dan menyetujui proses verifikasi untuk pengajuan pinjaman.</span></label>
          <button className="app-submit" type="submit" disabled={submitting}>
            {submitting ? <><span className="app-loader" /> Mengirim data…</> : 'Kirim Pengajuan →'}
          </button>
          <p className="app-security">🔒 Data Anda dilindungi dan hanya digunakan untuk proses pengajuan.</p>
        </form>
      </main>
    </div>
  )
}

export default ApplicationPage
